// src/components/company/ClientTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ref, onValue, off } from "firebase/database";
import { auth, firestore, database } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import ClientPaymentModal from "./ClientPaymentModal";
import EditClientModal from "./EditClientModal";
import RecordPurchaseModal from "./RecordPurchaseModal";
import ViewClientModal from "./ViewClientModal";
import SendInvoiceModal from "./SendInvoiceModal";
import { useDebounce } from "../../../utils/useDebounce";
import { maskPhoneNumber } from "../../../utils/dataMasking";
import { useIsCompanyAdmin } from "../../../utils/useUserRole";

const ClientTable = ({ companyName: companyNameProp, compact = false }) => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState(companyNameProp || "");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const db = database;
  const isCompanyAdmin = useIsCompanyAdmin(companyName);

  // Debounce search term to reduce filtering on every keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Memoized filtered clients based on debounced search term
  const filteredClients = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return clients;
    }
    const lower = debouncedSearchTerm.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.phone.toLowerCase().includes(lower) ||
        c.route.toLowerCase().includes(lower)
    );
  }, [debouncedSearchTerm, clients]);

  // Detect logged-in user and fetch company name
  useEffect(() => {
    if (companyNameProp) {
      setCompanyName(companyNameProp);
      return undefined;
    }

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const cachedCompany = localStorage.getItem(`companyName_${user.uid}`);
        if (cachedCompany) {
          setCompanyName(cachedCompany);
        } else {
          try {
            const docSnap = await getDoc(doc(firestore, "users", user.uid));
            if (docSnap.exists()) {
              const data = docSnap.data();
              if (data.companyName) {
                setCompanyName(data.companyName);
                localStorage.setItem(`companyName_${user.uid}`, data.companyName);
              }
            }
          } catch (err) {
            console.error("Error fetching company name:", err);
            toast.error("Failed to fetch company name");
          }
        }
      } else {
        setCompanyName("");
        setClients([]);
      }
    });
    return () => unsubscribe();
  }, [companyNameProp]);

  // Fetch clients from Firebase
  useEffect(() => {
    if (!companyName || !db) return;

    const clientsRef = ref(db, `Companies/${companyName}/ValidatedClients`);
    setLoading(true);

    onValue(
      clientsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.entries(data).map(([key, value]) => ({
            id: key,
            recordId: value.Id || "",
            name: value.CloudName || "Unnamed Client",
            cloudName: value.CloudName || value.cloudName || value.Name,
            otherName: value.OtherName || key,
            companyName: value.CompanyName || companyName,
            location: value.Location || "",
            address: value.Location || "N/A",

            phone: value.PhoneNumber || "N/A",
            route: value.Route || "N/A",
            balance: value.Balance || 0,
            status: value.Status || "Active",
            createdAt: value.CreatedAt || "",
            mode: value.Mode || "Credit",
            linkedShop: Boolean(value.LinkedShop),
            unitPrice: parseFloat(value.UnitPrice ?? value.unitPrice) > 0 ? parseFloat(value.UnitPrice ?? value.unitPrice) : null,
            paybill: value.PaybillNumber || "N/A",
            email: value.Email || "",
          }));
          setClients(list);
        } else {
          setClients([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading clients:", error);
        toast.error("Failed to load clients");
        setLoading(false);
      }
    );

    return () => {
      off(clientsRef);
    };
  }, [companyName, db]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleEditClick = useCallback((client) => {
    setSelectedClient(client);
    setShowEditModal(true);
  }, []);

  const handlePayClick = useCallback((client) => {
    setSelectedClient(client);
    setShowPaymentModal(true);
  }, []);

  const handlePurchaseClick = useCallback((client) => {
    setSelectedClient(client);
    setShowPurchaseModal(true);
  }, []);

  const handleViewClick = useCallback((client) => {
    setSelectedClient(client);
    setShowViewModal(true);
  }, []);

  const handleInvoiceClick = useCallback((client) => {
    setSelectedClient(client);
    setShowInvoiceModal(true);
  }, []);

  return (
    <div className="overflow-x-auto overflow-y-auto mt-4 max-h-[65vh] rounded-lg border border-gray-100 shadow-sm bg-white">
      {/* 🔍 Search bar */}
      {!compact && (
        <div className="flex justify-between items-center mb-3">
          <input
            type="text"
            placeholder="Search client by name, phone, or route..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="border px-3 py-2 rounded-lg w-80 focus:outline-none focus:ring-2 focus:ring-cyan-600"
          />
        </div>
      )}

      {/* Table */}
      <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <thead className="bg-cyan-600 text-white">
          <tr>
            <th className="text-left py-3 px-4 rounded-tl-lg">Client Name</th>
            {!compact && <th className="text-left py-3 px-4">Phone</th>}
            {!compact && <th className="text-left py-3 px-4">Route</th>}
            {!compact && <th className="text-left py-3 px-4">Balance (KES)</th>}
            {!compact && <th className="text-left py-3 px-4">Mode</th>}
            {!compact && <th className="text-left py-3 px-4">Status</th>}
            <th className="text-center py-3 px-4 rounded-tr-lg">Actions</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={compact ? 2 : 7} className="text-center py-6 text-gray-500 italic">
                Loading clients...
              </td>
            </tr>
          ) : filteredClients.length === 0 ? (
            <tr>
              <td colSpan={compact ? 2 : 7} className="text-center py-6 text-gray-500 italic">
                No clients found.
              </td>
            </tr>
          ) : (
            filteredClients.map((client, index) => (
              <tr
                key={client.id}
                className={`border-t hover:bg-cyan-50 transition ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <td className="py-3 px-4 font-medium text-gray-800">{client.name}</td>
                {!compact && <td className="py-3 px-4 text-gray-600">{isCompanyAdmin ? client.phone : maskPhoneNumber(client.phone)}</td>}
                {!compact && <td className="py-3 px-4 text-gray-600">{client.route}</td>}
                {!compact && (
                  <td className="py-3 px-4 text-gray-700">
                    {Number(client.balance).toLocaleString()}
                  </td>
                )}
                {!compact && <td className="py-3 px-4 text-gray-600">{client.mode}</td>}
                {!compact && (
                  <td
                    className={`py-3 px-4 font-semibold ${
                      client.status === "Active"
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {client.status}
                  </td>
                )}
                <td className="py-3 px-4 text-center space-x-2">
                  <button
                    className="bg-cyan-600 text-white px-3 py-1 rounded-md hover:bg-cyan-700 text-sm"
                    onClick={() => handleViewClick(client)}
                  >
                    View
                  </button>
                  <button
                    className="bg-amber-500 text-white px-3 py-1 rounded-md hover:bg-amber-600 text-sm"
                    onClick={() => handleEditClick(client)}
                  >
                    Edit
                  </button>
                  <button
                    className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-sm"
                    onClick={() => handlePurchaseClick(client)}
                  >
                    Record Purchase
                  </button>
                  <button
                    className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm"
                    onClick={() => handlePayClick(client)}
                  >
                    Pay
                  </button>
                  <button
                    className="bg-purple-600 text-white px-3 py-1 rounded-md hover:bg-purple-700 text-sm"
                    onClick={() => handleInvoiceClick(client)}
                  >
                    Send Invoice
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Summary */}
      {!compact && (
        <div className="mt-6 p-4 bg-cyan-50 border border-cyan-100 rounded-lg">
          <p className="text-sm text-cyan-800">
            💡 Tip: Use the search bar to quickly find clients by name or route.
            Click “Edit” to modify client details or “Record Purchase” to log a sale.
          </p>
        </div>
      )}

      {/* Modals */}
      {showPaymentModal && (
        <ClientPaymentModal
          client={selectedClient}
          companyName={companyName}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
      {showEditModal && selectedClient && (
        <EditClientModal
          client={selectedClient}
          companyName={companyName}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showPurchaseModal && (
        <RecordPurchaseModal
          client={selectedClient}
          companyName={companyName}
          onClose={() => setShowPurchaseModal(false)}
        />
      )}
      {showViewModal && (
        <ViewClientModal
          client={selectedClient}
          companyName={companyName}
          onClose={() => setShowViewModal(false)}
        />
      )}
      {showInvoiceModal && selectedClient && (
        <SendInvoiceModal
          open={showInvoiceModal}
          client={selectedClient}
          companyName={companyName}
          adminInfo={currentUser}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}
    </div>
  );
};

export default ClientTable;
